import { css } from "@emotion/css";
import { ActionButtonType, EmptyStateInsightsIllustration, ExternalLink, Level2OuterPageLoading } from "@octopusdeploy/design-system-components";
import type { PrimaryPageAction, PageTitleBadge } from "@octopusdeploy/design-system-components";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import type { InsightsReportResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { OctoLink } from "@octopusdeploy/utilities";
import type React from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import type { AnalyticSimpleDispatcher } from "~/analytics/Analytics";
import AddInsightsReportDialog from "~/areas/insights/components/Reports/AddInsightsReportDialog";
import { InsightsReportCard } from "~/areas/insights/components/Reports/ReportCard/ReportCard";
import { repository } from "~/clientInstance";
import DataBaseComponent from "~/components/DataBaseComponent";
import DataLoader from "~/components/DataLoader";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import { Level1EmptyStateLayout } from "~/components/GettingStarted/Level1EmptyStateLayout";
import LoadMoreCard from "~/components/LoadMoreCard/LoadMoreCard";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { createErrorInfoFromErrors } from "~/components/PageContent/createErrorInfo";
import { PermissionCheck } from "~/components/PermissionCheck";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import styles from "./style.module.less";
type InitialData = {
    reports: InsightsReportResource[];
    totalNumberOfReports: number;
    isTrial: boolean;
};
const ReportDataLoader = DataLoader<InitialData>();
interface ReportListLayoutProps {
    isTrial: boolean;
    children: React.ReactNode;
}
interface ReportOnboardingProps {
    isTrial: boolean;
}
interface ReportListInnerProps {
    initialReports: InsightsReportResource[];
    pageSize: number;
    totalNumberOfReports: number;
    dispatchAction: AnalyticSimpleDispatcher;
}
interface ReportListInnerState {
    reports: InsightsReportResource[];
    currentPage: number;
}
function AddInsightsReportButton() {
    const dispatchAction = useAnalyticSimpleActionDispatch();
    const onClick = () => dispatchAction("Click Create Insights Report Button");
    const { navigate } = useSpaceAwareNavigation();
    const onReportCreated = (report: InsightsReportResource) => {
        navigate(links.insightsReportSettingsPage.generateUrl({ spaceId: report.SpaceId, reportId: report.Id }));
    };
    return (<>
            <PermissionCheck permission={Permission.InsightsReportCreate}>
                <OpenDialogButton type={ActionButtonType.Primary} label="Create Report" onClick={onClick}>
                    <AddInsightsReportDialog onReportCreated={onReportCreated}/>
                </OpenDialogButton>
            </PermissionCheck>
            <ExternalLink href={OctoLink.Create("SpaceLevelDevOpsInsights")}>Learn More</ExternalLink>
        </>);
}
export function ReportOnboarding({ isTrial }: ReportOnboardingProps) {
    const intro = (<span className={reportOnboardingStyles.intro}>
            Space-level DevOps Insights gives you a unified view of DORA metrics (deployment speed, failure rates, and recovery times) across projects, so you can see what’s slowing deployments and optimize at scale.
        </span>);
    const upsellBadge: PageTitleBadge | undefined = isTrial ? { variant: "upsell", label: "Enterprise" } : undefined;
    return <Level1EmptyStateLayout badge={upsellBadge} title={"See deployment trends across projects with space-level DevOps Insights"} intro={intro} image={<EmptyStateInsightsIllustration />} actionButtonsAndLinks={[<AddInsightsReportButton />]}/>;
}
function ReportListLayout({ isTrial, children }: ReportListLayoutProps) {
    const { isOpen: isCreateReportDialogOpen, openDialog: openCreateReportDialog, closeAllDialogs: closeCreateReportDialog } = useDialogTrigger();
    const dispatchAction = useAnalyticSimpleActionDispatch();
    const onClickCreateInsightsReportButton = () => {
        dispatchAction("Click Create Insights Report Button");
        openCreateReportDialog();
    };
    const { navigate } = useSpaceAwareNavigation();
    const onReportCreated = (report: InsightsReportResource) => {
        navigate(links.insightsReportSettingsPage.generateUrl({ spaceId: report.SpaceId, reportId: report.Id }));
        closeCreateReportDialog();
    };
    const createInsightsReportAction: PrimaryPageAction = {
        type: "button",
        label: "Create an Insights Report",
        onClick: onClickCreateInsightsReportButton,
        hasPermissions: isAllowed({
            permission: Permission.InsightsReportCreate,
        }),
    };
    const upsellBadge: PageTitleBadge | undefined = isTrial ? { variant: "upsell", label: "Enterprise" } : undefined;
    return (<PageContent header={{ title: "Insights", badge: upsellBadge, primaryAction: createInsightsReportAction }}>
            <Dialog open={isCreateReportDialogOpen}>
                <AddInsightsReportDialog onReportCreated={onReportCreated}/>
            </Dialog>
            {children}
        </PageContent>);
}
class ReportListInner extends DataBaseComponent<ReportListInnerProps, ReportListInnerState> {
    constructor(props: ReportListInnerProps) {
        super(props);
        this.state = {
            currentPage: 1,
            reports: [...props.initialReports],
        };
    }
    componentDidMount(): void {
        this.props.dispatchAction("View Insights Report List");
    }
    private async loadMoreReports() {
        const { currentPage, reports } = this.state;
        const { pageSize } = this.props;
        this.doBusyTask(async () => {
            const nextReports = (await repository.InsightsReports.list({ skip: currentPage * pageSize, take: pageSize })).Items;
            this.setState({ currentPage: currentPage + 1, reports: [...reports, ...nextReports] });
        });
    }
    render() {
        const { reports } = this.state;
        const { totalNumberOfReports } = this.props;
        const showLoadMore = reports.length < totalNumberOfReports;
        return (<main>
                <>
                    {reports.length > 0 && (<div className={styles.castListContainer}>
                            <div className={styles.cardList}>
                                {reports.map((w) => (<InsightsReportCard key={w.Id} report={w}/>))}
                                {/* This design is not finalized. This load more button will change */}
                                {showLoadMore && <LoadMoreCard onLoadAll={() => this.loadMoreReports()} onLoadMore={() => this.loadMoreReports()}/>}
                            </div>
                        </div>)}
                </>
            </main>);
    }
    static displayName = "ReportListInner";
}
export function InsightsReportsPage() {
    const pageSize = 30;
    const dispatchAction = useAnalyticSimpleActionDispatch();
    return (<ReportDataLoader load={async () => {
            const insightsReportsPromise = repository.InsightsReports.list({ skip: 0, take: pageSize });
            const licenseStatusPromise = repository.Licenses.getCurrentStatus();
            const insightsReports = await insightsReportsPromise;
            const licenseStatus = await licenseStatusPromise;
            return { reports: insightsReports.Items, totalNumberOfReports: insightsReports.TotalResults, isTrial: licenseStatus.IsTrial };
        }} renderAlternate={({ errors }) => {
            const errorInfo = errors ? createErrorInfoFromErrors(errors) : undefined;
            return <Level2OuterPageLoading errors={errorInfo}/>;
        }} renderWhenLoaded={({ reports, totalNumberOfReports, isTrial }) => {
            if (reports.length === 0)
                return <ReportOnboarding isTrial={isTrial}/>;
            return (<ReportListLayout isTrial={isTrial}>
                        <ReportListInner initialReports={reports} pageSize={pageSize} totalNumberOfReports={totalNumberOfReports} dispatchAction={dispatchAction}/>
                    </ReportListLayout>);
        }}/>);
}
const reportOnboardingStyles = {
    intro: css({
        color: themeTokens.color.text.secondary,
    }),
};
